/**
 * @module Strings
 */

/**
 * Convenience function to convert a camelCase string to snake_case by converting all uppercase letters in the specified string into an underscore followed by the lowercase version of the letter.
 *
 * @param {string} camelCaseString - The camel case string value to convert.
 * @param {Array} exceptions - Array of string exceptions to re-convert if found (e.g. [['_i_d', '_id'], ['_u_r_l', '_url']]).
 *
 * @returns {string} Converted snake case string.
 */
function camelToSnake(camelCaseString, exceptions) {
  if (!camelCaseString || typeof camelCaseString !== 'string') {
    return '';
  }
  let snakeCaseString = camelCaseString
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase();
  snakeCaseString = snakeCaseString.replace(/-/g, '_');
  if (exceptions && Array.isArray(exceptions)) {
    exceptions.forEach((exc) => {
      if (Array.isArray(exc)) {
        snakeCaseString = snakeCaseString.replace(exc[0], exc[1]);
      }
    });
  }
  return snakeCaseString;
}

/**
 * Convenience function to convert the specified string value to a slug, replacing spaces with hyphens, and removing all non-alphanumeric characters.
 *
 * @param {string} string - The string value to convert to a hyphen-separated slug value.
 *
 * @returns {string} The converted string value as a slug (Example: 'Creative, Content, Communications' -> 'creative-content-communications').
 */
function createSlugFromString(string) {
  if (typeof string !== 'string') {
    return string;
  }
  return string
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .replaceAll(' ', '-')
    .toLowerCase();
}

/**
 * Convenience function to inspect the provided string and return its file extension.
 *
 * @param {string} string - The string to inspect and return the file extension (the last portion of the string after the last '.' found).
 *
 * @returns {string} The file extension. (Example: 'gif', 'png').
 */
function getFileExtension(string) {
  if (!string || typeof string !== 'string') {
    return null;
  }
  const regExp = /[^a-z0-9.]/i;
  const fileExt = string.split('.').pop();
  const firstNonAlphanumeric = fileExt.search(regExp);
  return firstNonAlphanumeric >= 0
    ? fileExt.substring(0, firstNonAlphanumeric)
    : fileExt;
}

/**
 * Convenience function to return a letter corresponding to the number passed in.
 *
 * @param {number} number - The number for the corresponding UTF-16 character in [a-z] for our case.
 *
 * @returns {string} - The character found in this case a letter. (e.g 0 -> 65+0 -> "A"). If the number is not between 0 and 26 (inclusive), an empty string is returned.
 */
function getLetterForNumber(number) {
  return number >= 0 && number < 26 ? String.fromCharCode(65 + number) : '';
}

module.exports = {
  camelToSnake,
  createSlugFromString,
  getFileExtension,
  getLetterForNumber,
};
